import { useCallback, useContext, useState } from "react";
import { LanguageContext } from "../../utils/context/languageContext";
import Header from "../Header";
import { MenuItem, Select } from "@mui/material";
import SpecificButton from "../../atoms/specificButton/SpecificButton";
import { ArrowRight } from "lucide-react";
import { useConfig } from "../../utils/context/configContext";
import { FocalLengthProps } from "../../types/interfaces";
import DevicesList from "../../utils/deviceFocalLengthJson";
import { handleErrorMessage } from "../../utils/utils";
import { LanguageKeys } from "../../utils/languageKeys";

type PhoneModel = {
	id: number;
	model_name: string;
	focal_length: number;
};

type DeviceManufacturer = Record<string, PhoneModel[]>;
type DevicesListType = DeviceManufacturer[];

const FocalLengthWrapper = ({ onComplete }: Omit<FocalLengthProps, "config">) => {
	const { translate } = useContext(LanguageContext) || {};
	const resolvedConfig = useConfig();
	const [deviceInfo, setDeviceInfo] = useState({
		brandName: "",
		model: null as PhoneModel | null,
	});
	const [message, setMessage] = useState<string | undefined>(undefined);
	const [loading, setLoading] = useState(false);

	/* ------------ Brand Menu ------------ */
	const manufacturerMenuItems = useCallback(() => {
		return (DevicesList as DevicesListType).map((manufacturer) => {
			const brandName = Object.keys(manufacturer)[0];
			return (
				<MenuItem key={brandName} value={brandName}>
					{brandName}
				</MenuItem>
			);
		});
	}, []);

	/* ------------ Model Menu ------------ */
	const phoneModelMenuItems = useCallback(() => {
		const brandMatch = (DevicesList as DevicesListType).find((manufacturer) => {
			const key = Object.keys(manufacturer)[0];
			return key === deviceInfo.brandName;
		});

		if (!brandMatch) return null;

		const key = Object.keys(brandMatch)[0];
		const models = brandMatch[key];

		return models.map((phone) => (
			<MenuItem key={phone.id} value={phone.id}>
				{phone.model_name}
			</MenuItem>
		));
	}, [deviceInfo.brandName]);

	const handleSubmit = useCallback(async () => {
		setLoading(true);
		try {
			if (deviceInfo.brandName && deviceInfo.model) {
				await onComplete?.({
					brandName: deviceInfo.brandName,
					modelName: deviceInfo.model.model_name,
					focalLength: deviceInfo.model.focal_length,
				});
			}
		} catch (error) {
			setMessage(handleErrorMessage(error));
		} finally {
			setLoading(false);
		}
	}, [deviceInfo]);

	/* ------------ Brand Change ------------ */
	const handleBrandChange = (event: any) => {
		setDeviceInfo({
			brandName: event.target.value,
			model: null,
		});
	};

	/* ------------ Model Change ------------ */
	const handleModelChange = (event: any) => {
		const selectedId = event.target.value;

		const brandMatch = (DevicesList as DevicesListType).find((m) => {
			return Object.keys(m)[0] === deviceInfo.brandName;
		});

		if (!brandMatch) return;

		const key = Object.keys(brandMatch)[0];
		const models = brandMatch[key];

		const foundModel = models.find((phone) => phone.id === selectedId);

		setDeviceInfo((prev) => ({
			...prev,
			model: foundModel ?? null,
		}));
	};


	return (
		<div
			data-testid="focal-length-root"
			className="h-full common-ui-main flex-col max-w-md mx-auto pt-[1rem] p-[15px] w-full flex justify-start items-start text-center rounded-t-[20px]"
			style={{ background: resolvedConfig?.style?.base?.backgroundColor }}
		>
			<div className="w-full max-w-[28rem] mx-auto">
				<Header subtitle={translate?.(LanguageKeys.phoneModel)} />
				<div className="text-left mb-[.25rem] mt-[1rem]">{translate?.(LanguageKeys.selectPhoneBrand)}</div>

				<div data-testid="focal-length-brand-select">
					<Select
						onChange={handleBrandChange}
						className="w-full h-[40px] !shadow-none !outline-none text-left"
						value={deviceInfo.brandName}
						style={{
							background: resolvedConfig?.style?.input?.inputBackgroundColor || "#F9FAFC",
							color: resolvedConfig?.style?.input?.inputTextColor || "#000",
							fontSize: resolvedConfig?.style?.input?.inputFontSize || "16px",
							fontWeight: resolvedConfig?.style?.input?.inputFontWeight || "400",
							border: `1px solid ${resolvedConfig?.style?.input?.inputBorderColor || "transparent"}`,
							fontFamily: resolvedConfig?.style?.base?.baseFontFamily || "Inter, sans-serif",
						}}
					>
						{manufacturerMenuItems()}
					</Select>
				</div>

				<div className="text-left mb-[.25rem] mt-[1rem]">{translate?.(LanguageKeys.selectPhoneModel)}</div>

				<div data-testid="focal-length-model-select">
					<Select
						onChange={handleModelChange}
						className="w-full bg-btn h-[40px] !shadow-none !outline-none text-left"
						value={deviceInfo.model?.id || ""}
						disabled={!deviceInfo.brandName}
						style={{
							background: resolvedConfig?.style?.input?.inputBackgroundColor || "#F9FAFC",
							color: resolvedConfig?.style?.input?.inputTextColor || "#000",
							fontSize: resolvedConfig?.style?.input?.inputFontSize || "16px",
							fontWeight: resolvedConfig?.style?.input?.inputFontWeight || "400",
							border: `1px solid ${resolvedConfig?.style?.input?.inputBorderColor || "transparent"}`,
							fontFamily: resolvedConfig?.style?.base?.baseFontFamily || "Inter, sans-serif",
						}}
					>
						{phoneModelMenuItems()}
					</Select>
				</div>
				<style>
					{`
      .MuiOutlinedInput-notchedOutline {
        border: 1px solid ${resolvedConfig?.style?.input?.inputBorderColor || "transparent"} !important;
        outline: none;
      }
        .MuiSelect-select{
         outline: none;
        }
       
    `}
				</style>
				{message && <p className="mt-[0.2rem] text-[16px]">{message}</p>}
			</div>

			<div className="mt-[.75rem] mb-[1.25rem] max-w-[28rem] mx-auto w-full flex justify-end">
				<SpecificButton
					disabled={!deviceInfo?.brandName || !deviceInfo?.model || loading}
					data-testid="focal-length-next-btn"
					postfixIcon={<ArrowRight />}
					buttonFunc={handleSubmit}
					buttonText={translate?.(LanguageKeys.next)}
				/>
			</div>
		</div>
	);
};

export default FocalLengthWrapper;
